/**
 * #40 · La cadena de custodia, como **historial**.
 *
 * `roll_custody_event` es append-only: la API no tiene UPDATE ni DELETE sobre
 * esa tabla (ver los GRANT de `0005_catalog_labels.sql`). Esta vista respeta
 * eso literalmente — no hay un solo control editable acá dentro. Una cadena de
 * custodia que se puede corregir no responde nada, y una que se ve como un
 * campo con un desplegable invita a "arreglar" el estado en vez de registrar
 * el evento que lo cambió.
 *
 * Cada evento muestra su evidencia: de qué estado a cuál, cuándo ocurrió de
 * verdad (`occurred_at`, no `created_at`), dónde, con qué remito o acta, y
 * **cuántas unidades quedaron afectadas**. Ese último número es lo que
 * convierte "se marcó robado" en "se marcó robado y 27.000 etiquetas dejaron
 * de poder autenticar".
 */
import type { ReactNode } from 'react';

import { useKeysetQuery } from '@/api/hooks';
import { rollKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { CardBody } from '@/ui/Card';
import { cn } from '@/ui/cn';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { ROLL_STATE_LABEL_KEYS, ROLL_STATE_TONES, isRollState } from './custody';

type CustodyEvent = Schemas['CustodyEventOut'];

const PAGE_SIZE = 25;

export function CustodyTimeline({ rollId }: { rollId: number }): ReactNode {
  const { t, fmt } = useI18n();

  const events = useKeysetQuery<CustodyEvent>(
    rollKeys.custody(rollId, PAGE_SIZE),
    `/api/v1/rolls/${String(rollId)}/custody`,
    undefined,
    { limit: PAGE_SIZE },
  );
  const { query } = events;

  if (query.isPending) return <LoadingState rows={4} />;
  if (query.isError) {
    return (
      <CardBody>
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </CardBody>
    );
  }
  if (events.items.length === 0) {
    return <EmptyState title={t('labels.custody.empty')} detail={t('labels.custody.empty.detail')} />;
  }

  return (
    <CardBody>
      <ol className="space-y-0">
        {events.items.map((event, index) => {
          const critical = event.to_state === 'stolen';
          return (
            <li key={event.id} className="relative flex gap-4 pb-6 last:pb-0">
              {/* La línea que une los hitos. El último no la lleva. */}
              {index < events.items.length - 1 && (
                <span
                  aria-hidden="true"
                  className="absolute top-4 left-[7px] h-full w-px bg-border"
                />
              )}
              <span
                aria-hidden="true"
                className={cn(
                  'mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full border-2',
                  critical ? 'border-critical bg-critical' : 'border-border-strong bg-surface',
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    tone={isRollState(event.to_state) ? ROLL_STATE_TONES[event.to_state] : 'neutral'}
                  >
                    {isRollState(event.to_state)
                      ? t(ROLL_STATE_LABEL_KEYS[event.to_state])
                      : event.to_state}
                  </Badge>
                  {event.from_state !== null && event.from_state !== undefined && (
                    <span className="text-xs text-ink-subtle">
                      {t('labels.custody.from', {
                        from: isRollState(event.from_state)
                          ? t(ROLL_STATE_LABEL_KEYS[event.from_state])
                          : event.from_state,
                      })}
                    </span>
                  )}
                  <span className="text-xs text-ink-muted">{fmt.dateTime(event.occurred_at)}</span>
                </div>

                <dl className="mt-1.5 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  <Line
                    label={t('labels.custody.units')}
                    value={
                      <span
                        className={cn(
                          'tabular-nums',
                          event.units_affected > 0 && critical && 'font-semibold text-critical',
                        )}
                      >
                        {fmt.number(event.units_affected)}
                      </span>
                    }
                  />
                  {event.location !== null && event.location !== undefined && (
                    <Line label={t('labels.custody.location')} value={event.location} />
                  )}
                  {event.reference !== null && event.reference !== undefined && (
                    <Line
                      label={t('labels.custody.reference')}
                      value={<code className="font-mono text-xs">{event.reference}</code>}
                    />
                  )}
                  {/* El actor va por id de usuario: es lo que devuelve la API.
                      Resolverlo a nombre exigiría un request por evento. */}
                  {event.actor_user_id !== null && event.actor_user_id !== undefined && (
                    <Line
                      label={t('labels.custody.actor')}
                      value={t('labels.custody.actor.value', { id: event.actor_user_id })}
                    />
                  )}
                </dl>

                {event.note !== null && event.note !== undefined && event.note !== '' && (
                  <p className="mt-1.5 rounded border border-border bg-surface-sunken px-3 py-2 text-sm text-ink">
                    {event.note}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="flex items-center justify-center border-t border-border pt-4">
        {events.hasMore ? (
          <Button size="sm" onClick={events.loadMore} disabled={events.isLoadingMore}>
            {events.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
          </Button>
        ) : (
          <p className="text-xs text-ink-subtle">{t('labels.custody.complete')}</p>
        )}
      </div>
    </CardBody>
  );
}

function Line({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="min-w-0">
      <dt className="inline text-xs tracking-wide text-ink-subtle uppercase">{label}: </dt>
      <dd className="inline text-sm break-words text-ink">{value}</dd>
    </div>
  );
}
